"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { AuthBrandHeader } from "@/components/AuthBrandHeader";
import { DeskBackButton } from "@/components/DeskBackButton";
import { AuthProviderButtons } from "@/components/AuthProviderButtons";
import { ExpiryDeskWordmark } from "@/components/ExpiryDeskWordmark";
import { HourglassLoader } from "@/components/HourglassLoader";
import { PharmacyQueueBackground } from "@/components/PharmacyQueueBackground";
import { PasswordVisibilityField } from "@/components/PasswordVisibilityField";
import { getAuthRedirectTo } from "@/lib/authRedirect";
import { signupPasswordChecks, validateEmailFormat, validateSignupPassword } from "@/lib/authValidators";
import { humanizeNetworkOrUnknown } from "@/lib/humanizeClientError";
import { supabase } from "@/lib/supabaseClient";

const ED = {
  font: 'var(--font-geist-sans), system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  text: "#0c4a6e",
  textSecondary: "#64748b",
  textMuted: "#94a3b8",
  r12: "12px",
  r16: "16px",
  ease: "cubic-bezier(0.4, 0, 0.2, 1)",
};

export default function SignupPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled && data.session?.user) router.replace("/dashboard");
    });
    return () => {
      cancelled = true;
    };
  }, [router]);

  const emailIssue = validateEmailFormat(email);
  const passwordIssue = validateSignupPassword(password);
  const pwCheck = signupPasswordChecks(password);
  const formComplete = !emailIssue && !passwordIssue && acceptedTerms;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    const emErr = validateEmailFormat(email);
    if (emErr) {
      setError(emErr);
      return;
    }
    const pwErr = validateSignupPassword(password);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    if (!acceptedTerms) {
      setError("Accept the Terms of use to continue.");
      return;
    }
    if (!supabase) {
      setError("Account creation isn't available right now. Please try again in a moment.");
      return;
    }
    setLoading(true);
    try {
      const redirectTo = getAuthRedirectTo();
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: redirectTo,
          data: {
            newsletter_opt_in: newsletterOptIn,
            terms_accepted_at: new Date().toISOString(),
          },
        },
      });
      if (signUpErr) {
        const msg = signUpErr.message || "Sign-up failed.";
        if (/password/i.test(msg) && !/email/i.test(msg)) {
          throw new Error("Password must be at least 8 characters and include a number and a symbol.");
        }
        throw signUpErr;
      }
      if (!signUpData?.user?.id) {
        setError("Could not create your account. Try again or log in if you already registered.");
        return;
      }
      setPassword("");
      if (typeof window !== "undefined" && signUpData.session?.access_token) {
        try {
          await fetch("/api/marketing/register-opt-in", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${signUpData.session.access_token}`,
            },
            body: JSON.stringify({ newsletter_opt_in: newsletterOptIn }),
          });
        } catch {
          /* non-blocking; metadata already set on signUp; user can sync on first login */
        }
      }
      setCheckEmail(true);
    } catch (err) {
      let msg = humanizeNetworkOrUnknown(err);
      if (/password|weak|credential/i.test(msg) && !/8\s*characters/i.test(msg)) {
        msg = "Password must be at least 8 characters and include a number and a symbol.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        padding: "1.5rem",
        fontFamily: ED.font,
        background: "linear-gradient(165deg, #ffffff 0%, #f0f7ff 45%, #e8f4fc 100%)",
        backgroundAttachment: "fixed",
      }}
    >
      <PharmacyQueueBackground position="absolute" />
      <main
        id="main"
        tabIndex={-1}
        style={{
          position: "relative",
          zIndex: 1,
          minHeight: "calc(100vh - 3rem)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
        }}
      >
      <div className="ed-card-glass ed-auth-card ed-fade-in" style={{ width: "100%", maxWidth: "400px", padding: "2rem 1.75rem", borderRadius: ED.r16 }}>
        <div style={{ marginBottom: "0.85rem" }}>
          <DeskBackButton fallbackHref="/" />
        </div>
        <header style={{ textAlign: "center", marginBottom: "1.1rem" }}>
          <AuthBrandHeader>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ExpiryDeskWordmark variant="auth" href="/" />
            </div>
          </AuthBrandHeader>
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.875rem", fontWeight: 500, color: ED.textSecondary, lineHeight: 1.45 }}>See what’s expiring before it becomes a write-off.</p>
        </header>

        {checkEmail ? (
          <div style={{ textAlign: "center" }}>
            <h1 style={{ fontSize: "1.125rem", fontWeight: 700, margin: "0 0 0.75rem", color: ED.text }}>Check your email</h1>
            <p style={{ margin: "0 0 1.25rem", fontSize: "0.9375rem", lineHeight: 1.55, color: ED.textSecondary }}>
              We emailed <strong style={{ color: ED.text }}>{email.trim()}</strong>. Open the link to activate — you’ll land on your dashboard.
            </p>
            <Link href="/dashboard" className="ed-btn-auth-solid" style={{ display: "inline-block", textDecoration: "none", padding: "0.75rem 1.25rem", fontFamily: ED.font }}>
              Back to log in
            </Link>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: "1.125rem", fontWeight: 700, margin: "0 0 0.5rem", color: ED.text, textAlign: "center", letterSpacing: "-0.02em" }}>Create an account</h1>
            <p style={{ margin: "0 0 1rem", fontSize: "0.875rem", lineHeight: 1.5, color: ED.textSecondary, textAlign: "center" }}>
              Use Google or email. With Google, verification happens at sign-in — no inbox confirmation step.
            </p>
            <AuthProviderButtons onError={setError} disabled={loading} />
            <p style={{ margin: "0.7rem 0 0", fontSize: "0.75rem", lineHeight: 1.45, color: ED.textMuted, textAlign: "center" }}>
              Fastest path: use Google to start immediately.
            </p>
            <p style={{ margin: "0.85rem 0 0", fontSize: "0.75rem", lineHeight: 1.45, color: ED.textMuted, textAlign: "center" }}>
              By continuing with Google, you agree to our{" "}
              <Link href="/terms" style={{ color: "#0284c7", fontWeight: 600, textDecoration: "none" }}>
                Terms of use
              </Link>
              .
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.65rem",
                margin: "1.15rem 0 1rem",
              }}
            >
              <div style={{ flex: 1, height: "1px", background: "rgba(100, 116, 139, 0.25)" }} />
              <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: ED.textMuted, whiteSpace: "nowrap" }}>or with email</span>
              <div style={{ flex: 1, height: "1px", background: "rgba(100, 116, 139, 0.25)" }} />
            </div>
            <form onSubmit={onSubmit}>
              <input
                className="ed-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Work email"
                type="email"
                autoComplete="email"
                aria-label="Work email"
                style={{ marginBottom: "0.65rem" }}
              />
              <PasswordVisibilityField
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Password"
                ariaLabel="Password"
                disabled={loading}
                invalid={password.length > 0 && !!validateSignupPassword(password)}
              />
              <p style={{ margin: "0.45rem 0 0", fontSize: "0.8125rem", fontWeight: 600, color: ED.textSecondary, lineHeight: 1.45 }}>
                Password must have at least 8 characters, 1 number, and 1 special character.
              </p>
              <ul
                style={{
                  margin: "0.45rem 0 0",
                  paddingLeft: "1.15rem",
                  fontSize: "0.8125rem",
                  color: ED.textMuted,
                  lineHeight: 1.5,
                  listStyle: "disc",
                }}
              >
                <li style={{ color: pwCheck.len ? "#15803d" : ED.textMuted }}>At least 8 characters</li>
                <li style={{ color: pwCheck.num ? "#15803d" : ED.textMuted }}>One number</li>
                <li style={{ color: pwCheck.sym ? "#15803d" : ED.textMuted }}>One symbol (!, @, #, …)</li>
              </ul>

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.6rem",
                  marginTop: "1rem",
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                  lineHeight: 1.45,
                  color: ED.textSecondary,
                }}
              >
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  style={{ marginTop: "0.2rem", width: "1rem", height: "1rem", flexShrink: 0, accentColor: "#0284c7" }}
                  aria-required="true"
                />
                <span>
                  I agree to the{" "}
                  <Link href="/terms" style={{ color: "#0284c7", fontWeight: 600, textDecoration: "none" }}>
                    Terms&nbsp;of&nbsp;use
                  </Link>
                  .
                </span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.6rem",
                  marginTop: "0.65rem",
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                  lineHeight: 1.45,
                  color: ED.textSecondary,
                }}
              >
                <input
                  type="checkbox"
                  checked={newsletterOptIn}
                  onChange={(e) => setNewsletterOptIn(e.target.checked)}
                  style={{ marginTop: "0.2rem", width: "1rem", height: "1rem", flexShrink: 0, accentColor: "#0284c7" }}
                />
                <span>
                  Send me product news and tips (roughly monthly). Unsubscribe anytime — account and expiry emails still flow.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !formComplete}
                title={!formComplete ? "Enter a valid email, a strong password, and accept the terms to continue." : undefined}
                className="ed-btn-auth-solid"
                style={{
                  marginTop: "1.15rem",
                  width: "100%",
                  padding: "0.9rem 1rem",
                  minHeight: "48px",
                  fontFamily: ED.font,
                  cursor: loading || !formComplete ? "not-allowed" : "pointer",
                  opacity: !formComplete && !loading ? 0.65 : 1,
                }}
              >
                {loading ? (
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.45rem" }}>
                    <HourglassLoader size={16} label="Creating your account" />
                    Please wait…
                  </span>
                ) : (
                  "Sign up"
                )}
              </button>
              {!formComplete && !loading && (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.75rem", color: ED.textMuted, textAlign: "center", lineHeight: 1.4 }}>
                  Finish the password checklist and accept the terms to continue.
                </p>
              )}
            </form>
            <p style={{ margin: "1.15rem 0 0", textAlign: "center", fontSize: "0.875rem", color: ED.textSecondary }}>
              Already have an account?{" "}
              <Link href="/dashboard" style={{ color: "#0284c7", fontWeight: 600, textDecoration: "none" }}>
                Log in
              </Link>
            </p>
            {error && (
              <div style={{ marginTop: "1rem", color: "#b91c1c", fontSize: "0.875rem", lineHeight: 1.45, textAlign: "center" }} role="alert">
                {error}
              </div>
            )}
          </>
        )}
      </div>
      </main>
    </div>
  );
}
